import { defineStore } from 'pinia';
import { ref, reactive, computed } from 'vue';
import { useApi } from '@/utils/axios';
import { REVENDA_ROTAS, PRODUTO_ROTAS } from '@/utils/apiRoutes';

export const useRevendaStore = defineStore('revenda', () => {
  // State
  const revendas = ref([]);
  const revenda = ref(null);
  const loading = ref(false);
  const error = ref(null);

  // Pagination state
  const pagination = reactive({
    currentPage: 1,
    total: 0,
    perPage: 15,
    lastPage: 1
  });

  const filtros = ref({
    idrevenda: '',
    idrevendedor: '',
    nomefantasia: '',
    cidade: '',
    estado: '',
    razaosocial: '',
    responsavel: '',
    responsavel_financeiro: '',
    cnpj: '',
    email: '',
    telefone: '',
    codigo_ativacao: '',
    cadastrador: '',
    data_inicial: '',
    data_final: '',
    ativo: '',
    gas_do_povo: '',
  });

  const lastRequestedPage = ref(null);

  // Add this new state property
  const poligonos = ref([]);

  // historicoMapas state
  const historicoMapas = ref([]);
  const historicoMapasPagination = reactive({
    currentPage: 1,
    total: 0,
    perPage: 15,
    lastPage: 1
  });

  // Fetch revendas for a specific page
  const buscarRevendasPage = async (page) => {
    // Prevent duplicate requests for the same page
    if (lastRequestedPage.value === page) {
      return revendas.value;
    }

    lastRequestedPage.value = page;
    loading.value = true;
    error.value = null;

    try {
      const params = JSON.parse(JSON.stringify(filtros.value));

      params.page = page;

      if (params.idrevenda) params.idrevenda = Number(params.idrevenda);
      if (params.idrevendedor) params.idrevendedor = Number(params.idrevendedor);
      if (params.cadastrador) params.cadastrador = Number(params.cadastrador);

      if (params.ativo !== '' && params.ativo !== undefined) {
        params.ativo = Number(params.ativo);
      }

      if (params.telefone) {
        params.telefone = params.telefone.replace(/\D/g, '');
      }

      if (params.estado) {
        params.estado = params.estado.toUpperCase().substring(0, 2);
      }

      if (params.cnpj) {
        params.cnpj = params.cnpj.replace(/\D/g, '');
      }

      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null || params[key] === undefined) {
          delete params[key];
        }
      });

      const response = await useApi(REVENDA_ROTAS.LISTAR).fetch({ params });

      if (response?.data) {
        revendas.value = response.data;

        if (response.meta) {
          pagination.currentPage = response.meta.current_page;
          pagination.total = response.meta.total;
          pagination.perPage = response.meta.per_page;
          pagination.lastPage = response.meta.last_page;
        }

        return response.data;
      } else {
        throw new Error('Resposta inválida da API');
      }
    } catch (err) {
      error.value = err.message || 'Erro ao buscar revendas';
      console.error('Erro ao buscar revendas:', err);
      return [];
    } finally {
      loading.value = false;
    }
  };

  const buscarRevendas = (page = 1) => {
    return buscarRevendasPage(page);
  };

  const buscarRevendaPorId = async (id) => {
    loading.value = true;
    error.value = null;

    try {
      const response = await useApi(REVENDA_ROTAS.REVENDA(id)).fetch();

      if (response?.data) {
        revenda.value = response.data;
        return response.data;
      } else {
        throw new Error('Revenda não encontrada');
      }
    } catch (err) {
      error.value = err.message || 'Erro ao buscar revenda';
      console.error('Erro ao buscar revenda:', err);
      return null;
    } finally {
      loading.value = false;
    }
  };

  const aplicarFiltros = () => {
    lastRequestedPage.value = null;
    return buscarRevendasPage(1);
  };

  const limparFiltros = () => {
    filtros.value = {
      idrevenda: '',
      idrevendedor: '',
      nomefantasia: '',
      cidade: '',
      estado: '',
      razaosocial: '',
      responsavel: '',
      responsavel_financeiro: '',
      cnpj: '',
      email: '',
      telefone: '',
      codigo_ativacao: '',
      cadastrador: '',
      data_inicial: '',
      data_final: '',
      ativo: 1,
      gas_do_povo: '',
    };

    lastRequestedPage.value = null;
    return buscarRevendasPage(1);
  };

  const temMaisDados = computed(() => {
    return pagination.currentPage < pagination.lastPage;
  });

  const ativarRevenda = async (revendaId) => {
    if (!revendaId) {
      return {
        success: false,
        message: 'ID da revenda não informado'
      };
    }

    try {
      const response = await useApi(REVENDA_ROTAS.ATIVAR_REVENDA(revendaId)).put();

      return {
        success: response.status === "success",
        message: response.message || 'Revenda ativada com sucesso!'
      };
    } catch (error) {
      console.error('Erro ao ativar revenda:', error);
      return {
        success: false,
        message: error.response?.message || 'Erro ao ativar revenda.'
      };
    }
  };

  const desativarRevenda = async (revendaId, tempo_recarga = 'paraSempre') => {
    if (!revendaId) {
      return {
        success: false,
        message: 'ID da revenda não informado'
      };
    }

    loading.value = true;
    error.value = null;

    try {
      const response = await useApi(REVENDA_ROTAS.DESATIVAR_REVENDA(revendaId)).put({ tempo_recarga });

      return {
        success: response.status === "success",
        message: 'Revenda desativada com sucesso!'
      };
    } catch (error) {
      console.error('Erro ao desativar revenda:', error);
      return {
        success: false,
        message: 'Erro ao desativar revenda.'
      };
    } finally {
      loading.value = false;
    }
  };

  const processarStatusLigacaoRevenda = async (revendaId) => {
    if (!revendaId) {
      return {
        success: false,
        message: 'ID da revenda não informado'
      };
    }

    // Tentar encontrar revenda localmente
    let revendaAtual = revendas.value.find(r => r.id === revendaId);
    if (!revendaAtual) {
      // tentar buscar do servidor
      try {
        const fetched = await buscarRevendaPorId(revendaId);
        if (fetched) {
          revendaAtual = fetched;
          const idx = revendas.value.findIndex(r => r.id === revendaId);
          if (idx === -1) revendas.value.unshift(fetched);
        }
      } catch (err) {
        // ignore, será tratado abaixo se a revenda não for encontrada
      }
    }

    if (!revendaAtual) {
      return { success: false, message: 'Revenda não encontrada' };
    }

    const desired = !Boolean(revendaAtual.ligacao_habilitada);
    return await alterarStatusLigacaoRevenda(revendaId, desired);
  };

  const alterarStatusLigacaoRevenda = async (revendaId, habilitar) => {
    if (!revendaId) {
      return {
        success: false,
        message: 'ID da revenda não informado'
      };
    }

    loading.value = true;
    error.value = null;

    try {
      let response;
      if (habilitar) {
        response = await useApi(REVENDA_ROTAS.ATIVAR_LIGACAO_REVENDA(revendaId)).post();
      } else {
        response = await useApi(REVENDA_ROTAS.DESATIVAR_LIGACAO_REVENDA(revendaId)).del();
      }

      const success = response && (response.status === 'success' || response.success === true);

      if (success) {
        // Atualiza estado local se a revenda estiver na lista ou no objeto revenda
        const idx = revendas.value.findIndex(r => r.id === revendaId);
        if (idx !== -1) {
          revendas.value[idx] = { ...revendas.value[idx], ligacao_habilitada: !!habilitar };
        }
        if (revenda.value && revenda.value.id === revendaId) {
          revenda.value.ligacao_habilitada = !!habilitar;
        }

        return {
          success: true,
          message: response.message || (habilitar ? 'Recebimento de ligações habilitado' : 'Recebimento de ligações desabilitado'),
          data: response.data || null
        };
      }

      return { success: false, message: response?.message || 'Erro ao alterar status de ligação.' };
    } catch (error) {
      console.error('Erro ao alterar status de ligação:', error);
      return {
        success: false,
        message: error?.response?.data?.message || error?.message || 'Erro ao alterar status de ligação.'
      };
    } finally {
      loading.value = false;
    }
  };

  const atualizarRevenda = async (id, data) => {
    if (!id) {
      return {
        success: false,
        message: 'ID da revenda não informado'
      };
    }

    loading.value = true;
    error.value = null;

    try {
      const formattedData = { ...data };

      if (formattedData.telefone) {
        formattedData.telefone = formattedData.telefone.replace(/\D/g, '');
      }

      if (formattedData.cnpj) {
        formattedData.cnpj = formattedData.cnpj.replace(/\D/g, '');
      }

      if (formattedData.estado) {
        formattedData.estado = formattedData.estado.toUpperCase().substring(0, 2);
      }

      // Ensure lat/lon are included in the payload
      if (formattedData.lat) {
        formattedData.lat = String(formattedData.lat);
      }

      if (formattedData.lon) {
        formattedData.lon = String(formattedData.lon);
      }

      const response = await useApi(REVENDA_ROTAS.REVENDA(id)).put(formattedData);

      if (response.status === "success") {
        if (revenda.value && revenda.value.id === id) {
          Object.assign(revenda.value, response.data || data);
        }

        return {
          success: true,
          message: response.message || 'Revenda atualizada com sucesso!',
          data: response.data
        };
      } else {
        throw new Error(response.message || 'Erro ao atualizar revenda');
      }
    } catch (err) {
      error.value = err.message || 'Erro ao atualizar revenda';
      console.error('Erro ao atualizar revenda:', err);

      return {
        success: false,
        message: err.message || 'Erro ao atualizar revenda'
      };
    } finally {
      loading.value = false;
    }
  };

  const limparDadosRevenda = () => {
    revenda.value = null;
  };

  const adicionarFormaPagamento = async (revendaId, idFormaPagamento) => {
    try {
      const response = await useApi(REVENDA_ROTAS.FORMAS_PAGAMENTO(revendaId)).post({ idFormaPagamento });
      return {
        success: true,
        message: response.message || 'Forma de pagamento adicionada com sucesso!',
        data: response.data
      };
    } catch (error) {
      console.error('Erro ao adicionar forma de pagamento:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Erro ao adicionar forma de pagamento.'
      };
    }
  };

  const removerFormaPagamento = async (revendaId, idFormaPagamento) => {
    try {
      const response = await useApi(REVENDA_ROTAS.FORMA_PAGAMENTO(revendaId, idFormaPagamento)).del();
      return {
        success: true,
        message: response.message || 'Forma de pagamento removida com sucesso!'
      };
    } catch (error) {
      console.error('Erro ao remover forma de pagamento:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Erro ao remover forma de pagamento.'
      };
    }
  };

  const atualizarProdutoRevenda = async (revendaId, produtoData) => {
    loading.value = true;
    error.value = null;

    try {
      const produtoId = produtoData.id;

      const endpoint = produtoId
        ? PRODUTO_ROTAS.PRODUTO(produtoId)
        : PRODUTO_ROTAS.PRODUTOS;

      const dataToSend = {
        ...produtoData,
        idrevenda: revendaId
      };

      const method = produtoId ? 'put' : 'post';

      const response = await useApi(endpoint)[method](dataToSend);

      if (response.status === "success") {
        await buscarRevendaPorId(revendaId);

        return {
          success: true,
          message: produtoId ? 'Produto atualizado com sucesso!' : 'Produto adicionado com sucesso!',
          data: response.data
        };
      } else {
        throw new Error(response.message || 'Erro ao processar produto');
      }
    } catch (err) {
      error.value = err.message || 'Erro ao processar produto';
      console.error('Erro ao processar produto:', err);

      return {
        success: false,
        message: err.message || 'Erro ao processar produto'
      };
    } finally {
      loading.value = false;
    }
  };

  /**
   * Buscar polígonos da revenda
   * @param {number|string} idrevenda - ID da revenda
   * @returns {Promise<Object>} - Resposta da API
   */
  const buscarPoligonosRevenda = async (idrevenda) => {
    loading.value = true;
    error.value = null;

    try {
      const response = await useApi(REVENDA_ROTAS.BUSCAR_POLIGONOS(idrevenda)).fetch();

      if (response && response.status === 'success' && response.data) {
        poligonos.value = response.data;
        return {
          success: true,
          data: response.data
        };
      } else {
        throw new Error(response?.message || 'Erro ao buscar polígonos da revenda');
      }
    } catch (err) {
      console.error('Erro ao buscar polígonos da revenda:', err);
      error.value = err.message || 'Erro ao buscar polígonos da revenda';
      return {
        success: false,
        message: err.message || 'Erro ao buscar polígonos da revenda'
      };
    } finally {
      loading.value = false;
    }
  };

  /**
   * Salvar polígonos da revenda
   * @param {number|string} idrevenda - ID da revenda
   * @param {Array} poligonosData - Array de arrays de coordenadas dos polígonos
   * @returns {Promise<Object>} - Resposta da API
   */
  const salvarPoligonosRevenda = async (idrevenda, poligonosData) => {
    loading.value = true;
    error.value = null;

    try {
      const response = await useApi(REVENDA_ROTAS.SALVAR_POLIGONOS(idrevenda)).post({
        poligonos: poligonosData
      });

      if (response && response.status === 'success') {
        poligonos.value = poligonosData; // Update local state with the saved data
        return {
          success: true,
          message: response.message || 'Áreas da revenda atualizadas com sucesso'
        };
      } else {
        throw new Error(response?.message || 'Erro ao salvar polígonos da revenda');
      }
    } catch (err) {
      console.error('Erro ao salvar polígonos da revenda:', err);
      error.value = err.message || 'Erro ao salvar polígonos da revenda';
      return {
        success: false,
        message: err.message || 'Erro ao salvar polígonos da revenda'
      };
    } finally {
      loading.value = false;
    }
  };

  /**
   * Buscar histórico de edições de mapas das revendas
   * @param {number} page - Número da página a ser buscada
   * @param {number} perPage - Itens por página
   * @returns {Promise<Object>} - Resposta da API
   */
  const fetchHistoricoMapas = async (page = 1, perPage = 15) => {
    loading.value = true;
    error.value = null;

    try {
      const api = useApi(REVENDA_ROTAS.HISTORICO_POLIGONOS);
      const params = {
        page,
        per_page: perPage
      };

      const response = await api.fetch({ params });

      if (response && response.status === 'success') {
        historicoMapas.value = response.data || [];

        if (response.meta) {
          historicoMapasPagination.currentPage = response.meta.current_page;
          historicoMapasPagination.total = response.meta.total;
          historicoMapasPagination.perPage = response.meta.per_page;
          historicoMapasPagination.lastPage = response.meta.last_page;
        }

        return {
          success: true,
          data: response.data
        };
      } else {
        throw new Error(response?.message || 'Erro ao buscar histórico de mapas');
      }
    } catch (err) {
      console.error('Erro ao carregar histórico de mapas:', err);
      error.value = err.message || 'Erro ao carregar o histórico de mapas das revendas';
      return {
        success: false,
        error: error.value
      };
    } finally {
      loading.value = false;
    }
  };

  return {
    // State
    revendas,
    revenda,
    loading,
    error,
    pagination,
    filtros,
    poligonos,
    temMaisDados,
    historicoMapas,
    historicoMapasPagination,

    // Methods
    buscarRevendas,
    buscarRevendasPage,
    buscarRevendaPorId,
    aplicarFiltros,
    limparFiltros,
    ativarRevenda,
    desativarRevenda,
    processarStatusLigacaoRevenda,
    alterarStatusLigacaoRevenda,
    atualizarRevenda,
    limparDadosRevenda,
    adicionarFormaPagamento,
    removerFormaPagamento,
    atualizarProdutoRevenda,
    buscarPoligonosRevenda,
    salvarPoligonosRevenda,
    fetchHistoricoMapas,
  };
});
